"use client";
import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

/* ─────────────────────────────────────────
   CUSTOM CURSOR
   - Dot: snaps instantly to mouse
   - Ring: lags behind with spring physics
   - Hover state: ring expands + blends
   - Hidden on touch devices automatically
───────────────────────────────────────── */
const Cursor = () => {
  const [hovered, setHovered] = useState(false);
  const [clicking, setClicking] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isTouch, setIsTouch] = useState(false);

  /* Raw mouse position */
  const mouseX = useMotionValue(-100);
  const mouseY = useMotionValue(-100);

  /* Spring-lagged ring position */
  const ringX = useSpring(mouseX, { stiffness: 120, damping: 18, mass: 0.6 });
  const ringY = useSpring(mouseY, { stiffness: 120, damping: 18, mass: 0.6 });

  useEffect(() => {
    /* Detect touch — hide cursor on touch devices */
    if (window.matchMedia("(pointer: coarse)").matches) {
      setIsTouch(true);
      return;
    }

    /* ── Mouse tracking ── */
    const onMove = (e) => {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
      if (!visible) setVisible(true);
    };

    /* ── Click feedback ── */
    const onDown = () => setClicking(true);
    const onUp = () => setClicking(false);

    /* ── Hover detection via event delegation ── */
    const onEnter = (e) => {
      if (e.target.closest("a, button, [data-cursor-hover], .hoverable")) {
        setHovered(true);
      }
    };
    const onLeave = (e) => {
      if (e.target.closest("a, button, [data-cursor-hover], .hoverable")) {
        setHovered(false);
      }
    };

    /* Hide when leaving window */
    const onMouseLeave = () => setVisible(false);
    const onMouseEnter = () => setVisible(true);

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("mouseover", onEnter);
    document.addEventListener("mouseout", onLeave);
    document.addEventListener("mouseleave", onMouseLeave);
    document.addEventListener("mouseenter", onMouseEnter);

    /* Hide native cursor globally */
    document.body.style.cursor = "none";

    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("mouseover", onEnter);
      document.removeEventListener("mouseout", onLeave);
      document.removeEventListener("mouseleave", onMouseLeave);
      document.removeEventListener("mouseenter", onMouseEnter);
      document.body.style.cursor = "";
    };
  }, []);

  /* Don't render on touch */
  if (isTouch) return null;

  return (
    <>
      {/* ── DOT — snaps instantly ── */}
      <motion.div
        className="fixed top-0 left-0 z-[9999] pointer-events-none"
        style={{
          x: mouseX,
          y: mouseY,
          translateX: "-50%",
          translateY: "-50%",
        }}
        animate={{
          scale: clicking ? 0.4 : hovered ? 0 : 1,
          opacity: visible ? 1 : 0,
        }}
        transition={{ duration: 0.15, ease: "easeOut" }}
      >
        <div
          className="w-2 h-2 rounded-full"
          style={{ background: "#00f0ff" }}
        />
      </motion.div>

      {/* ── RING — spring lag ── */}
      <motion.div
        className="fixed top-0 left-0 z-[9998] pointer-events-none"
        style={{
          x: ringX,
          y: ringY,
          translateX: "-50%",
          translateY: "-50%",
          borderRadius: "50%",
          border: "1px solid rgba(0,240,255,0.25)",
        }}
        animate={{
          width: clicking ? 28 : hovered ? 48 : 36,
          height: clicking ? 28 : hovered ? 48 : 36,
          opacity: visible ? 1 : 0,
          borderColor: hovered ? "rgba(0,240,255,0.6)" : "rgba(0,240,255,0.25)",
          backgroundColor: hovered ? "rgba(0,240,255,0.06)" : "transparent",
        }}
        transition={{
          width: { duration: 0.25, ease: [0.22, 1, 0.36, 1] },
          height: { duration: 0.25, ease: [0.22, 1, 0.36, 1] },
          opacity: { duration: 0.2 },
          borderColor: { duration: 0.25 },
          backgroundColor: { duration: 0.25 },
        }}
      />

      {/* ── TRAIL GLOW — subtle bloom behind ring ── */}
      <motion.div
        className="fixed top-0 left-0 z-[9997] pointer-events-none rounded-full"
        style={{
          x: ringX,
          y: ringY,
          translateX: "-50%",
          translateY: "-50%",
          width: 80,
          height: 80,
          background:
            "radial-gradient(circle, rgba(0,240,255,0.08) 0%, transparent 70%)",
          filter: "blur(8px)",
        }}
        animate={{ opacity: visible ? (hovered ? 1 : 0.5) : 0 }}
        transition={{ duration: 0.3 }}
      />
    </>
  );
};

export default Cursor;
